import React, { useState, useEffect } from 'react';
import { Button, Typography, Box, TextField } from '@mui/material';
import Rating from '@mui/material/Rating';

const MAX_COMMENT_LENGTH = 200;

const ChatRatingDialog = ({ open, prompt, thankYou, allowComments, onSubmit, onCancel, ratingSubmitted, onThankYouTimeout }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const inactivityTimerRef = React.useRef();

  // Reset inactivity timer
  const resetInactivityTimer = React.useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    inactivityTimerRef.current = setTimeout(() => {
      if (open && !ratingSubmitted && onCancel) {
        onCancel();
      }
    }, 120000); // 120 seconds
  }, [open, ratingSubmitted, onCancel]);

  // Start inactivity timer when dialog opens
  useEffect(() => {
    if (open && !ratingSubmitted) {
      resetInactivityTimer();
    }
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [open, ratingSubmitted, resetInactivityTimer]);

  useEffect(() => {
    if (ratingSubmitted) {
      const timer = setTimeout(() => {
        if (onThankYouTimeout) onThankYouTimeout();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [ratingSubmitted, onThankYouTimeout]);

  const handleSubmit = () => {
    if (rating > 0) {
      setSubmitted(true);
      onSubmit(rating, allowComments ? comment : undefined);
    }
  };

  if (!open) return null;
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        bgcolor: 'rgba(0,0,0,0.3)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 3,
          p: 4,
          minWidth: 280,
          maxWidth: 340,
          textAlign: 'center',
        }}
      >
        {!ratingSubmitted ? (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>{prompt}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
              <Rating
                name="chat-rating"
                value={rating}
                onChange={(_, value) => {
                  setRating(value);
                  resetInactivityTimer();
                }}
                size="large"
                max={5}
              />
              {allowComments && (
                <TextField
                  label="Comments (optional)"
                  multiline
                  minRows={2}
                  maxRows={4}
                  value={comment}
                  onChange={e => {
                    setComment(e.target.value.slice(0, MAX_COMMENT_LENGTH));
                    resetInactivityTimer();
                  }}
                  sx={{ mt: 2, width: '100%' }}
                  inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
                  placeholder="Add any comments (max 200 chars)"
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button onClick={onCancel} sx={{ flex: 1, mr: 1 }}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={rating === 0} variant="contained" color="primary" sx={{ flex: 1, ml: 1 }}>
                Submit
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>{thankYou}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChatRatingDialog;
